import React, { useState, useEffect } from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import TemplateDetailTabs from '@components/shared/templates/TemplateDetailTabs';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Card, Descriptions, Button, Empty, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAdminOrderTemplateService } from '@hooks/admin/useAdminOrderTemplateService';
import { useOperatorOrderTemplateService } from '@hooks/operator/order_templates/useOperatorOrderTemplateService';

import { formatDateTime } from '../../../utils/dateTime';

import { getActualPaperSize } from '@/constants/paperSizes';

// Import shared template components

/**
 * OrderTemplatesShow - Order Template detail view component
 *
 * @param {Object} props
 * @param {Object} props.orderTemplate - Order template data (loaded by loader)
 * @param {string} props.role - Role (admin or operator)
 */
export default function OrderTemplatesShow({
  orderTemplate,
  role = 'admin',
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Sample data with reserved_barcode_area loaded from API
  const [sampleOrderData, setSampleOrderData] = useState(null);
  const [sampleDataLoading, setSampleDataLoading] = useState(true);

  // Initialize service using hooks
  const adminService = useAdminOrderTemplateService();
  const operatorService = useOperatorOrderTemplateService({ merchantId: orderTemplate?.merchant_id });
  const service = role === 'operator' ? operatorService : adminService;

  // Calculate actual paper size from order template
  const actualPaperSize = orderTemplate ? getActualPaperSize(
    orderTemplate.paper_size_type || 'a4',
    orderTemplate.paper_width,
    orderTemplate.paper_height
  ) : { width: 210, height: 297 }; // Default to A4

  // Load sample data on mount and when language changes
  useEffect(() => {
    const loadSampleData = async () => {
      try {
        setSampleDataLoading(true);
        // i18n.language returns the current language code ('zh' or 'en')
        const locale = i18n.language || 'zh';

        // Import the module
        const { getSampleOrderData } = await import('@/constants/liquidTemplateData');

        // Get base data by locale (without reserved_barcode_area)
        const baseData = getSampleOrderData(locale);

        // Fetch reserved_barcode_area from API
        const response = await service.getSampleReservedBarcodeArea(locale);

        if (response.success && response.data && response.data.html) {
          setSampleOrderData({
            ...baseData,
            reserved_barcode_area: response.data.html
          });
        } else {
          console.warn('[loadSampleData] Failed to load reserved_barcode_area');
          setSampleOrderData(baseData);
        }
      } catch (error) {
        console.error('Failed to load sample order data:', error);
        // Fallback to default data
        const { SAMPLE_ORDER_DATA } = await import('@/constants/liquidTemplateData');
        setSampleOrderData(SAMPLE_ORDER_DATA);
      } finally {
        setSampleDataLoading(false);
      }
    };

    loadSampleData();
  }, [i18n.language, service]); // 當語系改變時也重新載入

  const handleEdit = () => {
    navigate(`/order_templates/${orderTemplate.id}/edit`);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('common.confirm.delete_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm.delete_content', { name: orderTemplate.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await service.delete(orderTemplate.id);
          if (response.success) {
            message.success(t('common.success.deleted'));
            navigate('/order_templates');
          } else {
            message.error(response.message || t('common.error.operation_failed'));
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error(t('common.error.operation_failed'));
        }
      },
    });
  };

  if (!orderTemplate) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? ['orders', 'orders.templates', { label: orderTemplate.name }]
    : ['orders', 'orders.templates', { label: orderTemplate.name }];

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={breadcrumbItems}
        actions={
          <>
            {orderTemplate.editable && (
              <Can requiredPermission="order_templates:write">
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                  {t('common.button.edit')}
                </Button>
              </Can>
            )}
            {orderTemplate.deletable && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                {t('common.button.delete')}
              </Button>
            )}
          </>
        }
      />

      <Card>
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('models.order_template.name')} span={2}>
            {orderTemplate.name || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.order_template.sales_channel_ids')}>
            {orderTemplate.sales_channels && orderTemplate.sales_channels.length > 0
              ? orderTemplate.sales_channels.map((c) => c.name).join('、')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.order_template.description')} span={2}>
            {orderTemplate.description || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.order_template.created_at')}>
            {orderTemplate.created_at ? formatDateTime(orderTemplate.created_at) : '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.order_template.updated_at')}>
            {orderTemplate.updated_at ? formatDateTime(orderTemplate.updated_at) : '-'}
          </Descriptions.Item>
        </Descriptions>

        {/* Template Detail Tabs (Shared Component) */}
        <TemplateDetailTabs
          content={orderTemplate.content}
          sampleData={sampleOrderData}
          paperSize={actualPaperSize}
          cssVariablePrefix="paper"
          t={t}
          namespace="shared.order_templates"
          modelName="order_template"
        />
      </Card>
    </div>
  );
}
